/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */
import { IS_LOADING } from '@scandipwa/scandipwa/src/store/Product/Product.action';

import { getIndexedProduct } from 'Util/Product';

import {
    UPDATE_BUNDLE_PRODUCT_PRICE,
    UPDATE_BUNDLE_PRODUCT_SELECTED_OPTIONS,
    UPDATE_PRODUCT_ADDITIONAL_INFO, UPDATE_PRODUCT_DETAILS
} from './Product.action';

/** @namespace Pwa/Store/Product/Reducer/getInitialState */
export const getInitialState = () => ({
    product: {},
    formattedConfigurableOptions: {},
    isLoading: false,
    selectedBundleOptions: [],
    bundleProductPrice: 0,
    bundleProductId: []
});

/** @namespace Pwa/Store/Product/Reducer/formatConfigurableOptions */
export const formatConfigurableOptions = (configurable_options) => configurable_options
    .reduce((prev, option) => {
        const {
            attribute_id,
            label,
            attribute_code
        } = option;

        return {
            ...prev,
            [attribute_code]: {
                attribute_id,
                label
            }
        };
    }, {});

/** @namespace Pwa/Store/Product/Reducer/ProductReducer */
export const ProductReducer = (
    state = getInitialState(),
    action
) => {
    switch (action.type) {
    case UPDATE_PRODUCT_DETAILS:
        const { product } = action;

        return {
            ...state,
            product: getIndexedProduct(product)
        };
    case UPDATE_PRODUCT_ADDITIONAL_INFO:
        const { additionalData } = action;
        const { product: pData } = state;
        const updatedProduct = { ...pData, additionalInformation: additionalData };

        return {
            ...state,
            product: updatedProduct
        };
    case IS_LOADING:
        const { value } = action;
        return {
            ...state,
            isLoading: value
        };
    case UPDATE_BUNDLE_PRODUCT_PRICE:
        const {
            productData: {
                id,
                price_range: {
                    minimum_price: { final_price: { value: productPrice } }
                }
            }
        } = action;

        // Fallbacks in case state is undefined
        const { bundleProductId = [], bundleProductPrice = 0 } = state;

        let updatedIds = Array.from(bundleProductId);
        let updatedPrice = bundleProductPrice;

        if (updatedIds.includes(id)) {
            // ID already present: remove it and subtract price
            updatedIds = updatedIds.filter((productId) => productId !== id);
            updatedPrice -= productPrice;
        } else {
            // ID not present: add it and add price
            updatedIds.push(id);
            updatedPrice += productPrice;
        }

        return {
            ...state,
            bundleProductId: updatedIds,
            bundleProductPrice: updatedPrice
        };
    case UPDATE_BUNDLE_PRODUCT_SELECTED_OPTIONS: {
        const { optionsData } = action;
        const { optionId, optionValue, quantity } = optionsData;

        const updated = state.selectedBundleOptions.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            value: Array.from(item.value)
        }));

        const existingIndex = updated.findIndex((item) => item.id === optionId);

        if (existingIndex !== -1) {
            const existing = updated[existingIndex];
            const valueIndex = existing.value.indexOf(String(optionValue));

            if (valueIndex !== -1) {
                existing.value.splice(valueIndex, 1);
            } else {
                existing.value.push(String(optionValue));
            }

            if (existing.value.length > 0) {
                existing.quantity = quantity;
                updated[existingIndex] = existing;
            } else {
                updated.splice(existingIndex, 1);
            }
        } else {
            updated.push({
                id: optionId,
                quantity,
                value: [String(optionValue)]
            });
        }

        return {
            ...state,
            selectedBundleOptions: updated
        };
    }
    default:
        return state;
    }
};

export default ProductReducer;
